/**
 * Generic category page with Bukiety-style hero.
 * Used for: Kosze kwiatowe, Kwiaty w torebce, Lasy w szkle,
 *           Balony z helem, Kwiaty doniczkowe, Wieczne róże.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'wouter';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';

import { fetchCollection, productDisplayPrice, type ShopifyCollection, type ShopifyProduct } from '@/lib/shopifyApi';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { displayText } from '@/lib/displayText';
import { collectionUrl } from '@/lib/collectionUrl';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { findBreadcrumbParent } from '@/components/sections/megaMenuData';
import { CollectionSeoSection } from '@/components/sections/CollectionSeoSection';
import { CategoryBuyingGuide } from '@/components/CategoryBuyingGuide';
import { REMAINING_P1_GUIDES } from '@/data/remainingP1SeoContent';
import { PRIORITY_P2_GUIDES } from '@/data/priorityP2SeoContent';
import { REMAINING_SEO_GUIDES } from '@/data/remainingSeoContent';
import { FlowerDeliveryBanner, FLOWER_HANDLES } from '@/components/sections/FlowerDeliveryBanner';
import { OkazjaBanner, OKAZJA_BANNER_HANDLES } from '@/components/sections/OkazjaBanner';
import { SpecjalnyBanner, SPECJALNY_BANNER_HANDLES } from '@/components/sections/SpecjalnyBanner';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { CmsSchema, useCmsFields } from '@/cms/content';
import {
  CollectionFilters,
  type FilterState,
  type Section,
  EMPTY_FILTERS,
  hasActiveFilters,
  OKAZJE,
  KWIATY,
} from '@/components/collection/CollectionFilters';
import {
  configBreadcrumbs,
  configCollectionContext,
  type CategoryPageConfig,
  type CategoryHeroImage,
} from './categoryPageConfig';

export type { CategoryHeroImage, CategoryPageConfig } from './categoryPageConfig';

/* ─── Design tokens ─────────────────────────────────────── */
const DARK  = '#2A1F14';
const GOLD  = '#e05590';
const ROSE  = '#9B2335';
const LINE  = 'rgba(210,60,120,0.18)';
const MUTED = 'rgba(42,31,20,0.45)';
const CREAM = '#FAF7F2';

const SORT_OPTIONS = [
  { label: 'Polecane',          value: 'COLLECTION_DEFAULT', reverse: false },
  { label: 'Najpopularniejsze', value: 'BEST_SELLING',       reverse: false },
  { label: 'Cena ↑',            value: 'PRICE',              reverse: false },
  { label: 'Cena ↓',            value: 'PRICE',              reverse: true  },
  { label: 'Najnowsze',         value: 'CREATED',            reverse: true  },
];

/* ─── Sort dropdown ──────────────────────────────────────── */
function SortDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(o => `${o.value}:${o.reverse}` === value) ?? SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '10px 20px', minHeight: 44,
          border: `1px solid ${open ? DARK : 'rgba(42,31,20,0.18)'}`,
          background: open ? DARK : '#fff',
          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
          letterSpacing: '0.13em', textTransform: 'uppercase',
          fontWeight: 600, color: open ? '#fff' : 'rgba(42,31,20,0.6)',
          cursor: 'pointer', transition: 'all 0.18s',
        }}
      >
        <SlidersHorizontal size={13} color={open ? 'rgba(255,255,255,0.7)' : GOLD} />
        {current.label}
        <ChevronDown size={11} color={open ? 'rgba(255,255,255,0.7)' : 'rgba(42,31,20,0.35)'}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${LINE}`,
          minWidth: 200, boxShadow: '0 12px 40px rgba(42,31,20,0.1)',
        }}>
          {SORT_OPTIONS.map(o => {
            const key = `${o.value}:${o.reverse}`;
            const active = value === key;
            return (
              <button key={key} onClick={() => { onChange(key); setOpen(false); }} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                background: active ? 'rgba(210,60,120,0.07)' : 'transparent',
                border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                color: active ? DARK : 'rgba(42,31,20,0.7)', fontWeight: active ? 600 : 400,
                borderLeft: active ? `2px solid ${GOLD}` : '2px solid transparent',
              }}>{o.label}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton ───────────────────────────────────────────── */
function SkeletonGrid() {
  return (
    <div className="col-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i}>
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)' }} className="animate-pulse" />
          <div style={{ padding: '20px 22px 24px', background: '#fff' }}>
            <div style={{ height: 20, background: 'rgba(210,60,120,0.07)', marginBottom: 10, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 17, background: 'rgba(210,60,120,0.07)', width: '35%' }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Filter helpers ─────────────────────────────────────── */
function normalizeTag(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
}

function applyFilters(
  products: ShopifyProduct[],
  filters: FilterState,
  tagMap?: Record<string, string[]>,
): ShopifyProduct[] {
  return products.filter(p => {
    const tags  = (p.tags ?? []).map(normalizeTag);
    const price = parseFloat(p.priceRange.minVariantPrice.amount);
    const productCollections = new Set((p.collections?.nodes ?? []).map(c => c.handle));
    const hasCollectionData  = p.collections !== undefined;

    // Okazje — collection-based (handles from shared OKAZJE constant)
    if (filters.okazje.length > 0) {
      const ok = filters.okazje.some(o => {
        const handle = OKAZJE.find(x => x.value === o)?.handle;
        if (!handle || !hasCollectionData) return tags.some(t => t.includes(normalizeTag(o)));
        return productCollections.has(handle);
      });
      if (!ok) return false;
    }

    // Kwiaty — collection-based (handles from shared KWIATY constant)
    if (filters.kwiaty.length > 0) {
      const ok = filters.kwiaty.some(k => {
        const handle = KWIATY.find(x => x.value === k)?.handle;
        if (!handle || !hasCollectionData) return tags.some(t => t.includes(normalizeTag(k)));
        return productCollections.has(handle);
      });
      if (!ok) return false;
    }

    // Kompozycje — tag-based (via configurable tagMap)
    if (filters.kompozycje.length > 0 && !filters.kompozycje.some(k => {
      const frags = ((tagMap ?? {})[k] ?? [k]).map(normalizeTag);
      return frags.some(frag => tags.some(t => t.includes(frag)));
    })) return false;

    // Price range
    if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;

    return true;
  });
}

const DEFAULT_VISIBLE_SECTIONS: Section[] = ['okazje', 'kwiaty', 'cena'];

/* ─── Page component ─────────────────────────────────────── */
export default function CategoryCollectionPage({ config }: { config: CategoryPageConfig }) {
  const accent = config.accentColor ?? ROSE;
  const cms = useCmsFields([
    { key: 'category.heading', label: 'Nagłówek', section: 'Wstęp', kind: 'text', defaultValue: config.h1Line1, required: true },
    { key: 'category.accent', label: 'Wyróżniona część nagłówka', section: 'Wstęp', kind: 'text', defaultValue: config.h1Accent, required: true },
    { key: 'category.description', label: 'Opis kategorii', section: 'Wstęp', kind: 'textarea', defaultValue: config.description, required: true },
  ]);

  const [collection, setCollection] = useState<ShopifyCollection | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [notFound,   setNotFound]   = useState(false);
  useSeo({ title: config.seoTitle, description: config.seoDescription, robots: notFound ? 'noindex, follow' : undefined });
  usePrerenderReady(!loading, notFound ? 404 : 200);
  const [sort,       setSort]       = useState('COLLECTION_DEFAULT:false');
  const [filters,    setFilters]    = useState<FilterState>(EMPTY_FILTERS);

  useEffect(() => {
    setLoading(true); setNotFound(false); setFilters(EMPTY_FILTERS);
    const [sortKey, reverseStr] = sort.split(':');
    fetchCollection(config.handle, { sort: sortKey, reverse: reverseStr === 'true', limit: 100 })
      .then(col => { if (!col) setNotFound(true); else setCollection(col); })
      .catch(err => {
        // A transient refresh failure must not turn an existing collection
        // into a 404 or empty state.
        console.warn(`[CategoryCollectionPage] refresh failed for ${config.handle}:`, err);
      })
      .finally(() => setLoading(false));
  }, [sort, config.handle]);

  const allProducts = (collection?.products.nodes ?? []).map(p => ({ ...p, tags: p.tags ?? [] }));

  const maxPrice = useMemo(() => {
    if (allProducts.length === 0) return 1000;
    return Math.ceil(Math.max(...allProducts.map(p => parseFloat(p.priceRange.minVariantPrice.amount))) / 50) * 50;
  }, [allProducts]);

  useEffect(() => {
    if (maxPrice > 0)
      setFilters(f => ({ ...f, priceRange: [f.priceRange[0], Math.max(f.priceRange[1], maxPrice)] }));
  }, [maxPrice]);

  const activeFilters = hasActiveFilters(filters, maxPrice);
  const filtered = useMemo(
    () => applyFilters(allProducts, filters, config.kompozycjaTagMap),
    [allProducts, filters, config.kompozycjaTagMap],
  );

  const availableOkazje = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return OKAZJE.filter(o => o.handle && handles.has(o.handle));
  }, [allProducts]);

  const availableKwiaty = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return KWIATY.filter(k => k.handle && handles.has(k.handle));
  }, [allProducts]);

  const parent = findBreadcrumbParent(config.handle);
  const breadcrumbs = configBreadcrumbs(config, parent);

  return (
    <>
      <CmsSchema fields={cms.fields} />
      <div style={{ background: '#fff', minHeight: '100vh' }}>
        <PageBreadcrumb crumbs={breadcrumbs} />

        {/* ── Page header ── */}
        <section style={{ background: '#fff', padding: 'max(clamp(48px,6vw,72px), calc(var(--page-breadcrumb-height, 44px) + 12px)) 0 clamp(32px,4vw,48px)' }}>
          <div className="page-container">
            <div>
              <h1 style={{
                fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 'clamp(2.2rem,4.5vw,3.8rem)', fontWeight: 400,
                color: DARK, margin: '0 0 16px', lineHeight: 1.06,
              }}>
                <span {...cms.text('category.heading')}>{displayText(cms.value('category.heading'))}</span> <span {...cms.text('category.accent')} style={{ color: accent }}>{displayText(cms.value('category.accent'))}</span>
              </h1>
              <p {...cms.text('category.description')} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, lineHeight: 1.7, color: MUTED, maxWidth: 500, margin: '0 0 12px', fontStyle: 'italic' }}>
                {displayText(cms.value('category.description'))}
              </p>
              {!loading && !notFound && (
                <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, margin: 0 }}>
                  Liczba {config.countLabel ?? 'produktów w ofercie'}: {allProducts.length}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── Filter bar + products ── */}
        <div className="page-container" style={{ background: '#fff' }}>

        {/* ── Filter bar ───────────────────────────────────── */}
        {!loading && !notFound && allProducts.length > 0 && (
          <div style={{
            background: '#ffffff',
            borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`,
          }}>
            <div style={{ padding: '14px 0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CollectionFilters
                    filters={filters}
                    onChange={setFilters}
                    maxPrice={maxPrice}
                    sortValue={sort}
                    onSortChange={setSort}
                    sortOptions={SORT_OPTIONS}
                    visibleSections={config.visibleSections ?? DEFAULT_VISIBLE_SECTIONS}
                    kompozycjeOptions={config.kompozycjeOptions}
                    kompozycjeLabel={config.kompozycjeLabel}
                    okazjeOptions={availableOkazje}
                    kwiatyOptions={availableKwiaty}
                  />
                </div>
                <SortDropdown value={sort} onChange={setSort} />
              </div>
            </div>
          </div>
        )}

        {/* ── Products ─────────────────────────────────────── */}
        <div style={{
          padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,120px)',
        }}>
          {loading && <SkeletonGrid />}

          {!loading && notFound && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 16 }}>
                Kolekcja nie istnieje
              </p>
              <Link href="/" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12,
                letterSpacing: '0.1em', color: GOLD, textDecoration: 'none', textTransform: 'uppercase' }}>
                Wróć na stronę główną →
              </Link>
            </div>
          )}

          {!loading && !notFound && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 8 }}>
                {activeFilters ? 'Brak produktów spełniających filtry' : 'Brak produktów'}
              </p>
              {activeFilters && (
                <button
                  onClick={() => setFilters({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] })}
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                    color: GOLD, background: 'none', border: 'none', cursor: 'pointer',
                    textTransform: 'uppercase', textDecoration: 'underline' }}
                >
                  Wyczyść filtry
                </button>
              )}
            </div>
          )}

          <style>{`
            .col-grid { grid-template-columns: repeat(4, 1fr); }
            @media (max-width: 768px)  { .col-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          `}</style>

          {!loading && filtered.length > 0 && (
            <div className="col-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
              {filtered.map((product, i) => (
                <CollectionProductCard
                  key={product.handle}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={configCollectionContext(config, collectionUrl(config.handle))}
                  centerInfo
                />
              ))}
            </div>
          )}
        </div>
        </div>{/* end white section */}

      </div>
      {collection && (
        REMAINING_P1_GUIDES[config.handle] ? <CategoryBuyingGuide content={REMAINING_P1_GUIDES[config.handle]} /> :
        PRIORITY_P2_GUIDES[config.handle] ? <CategoryBuyingGuide content={PRIORITY_P2_GUIDES[config.handle]} /> :
        REMAINING_SEO_GUIDES[config.handle] ? <CategoryBuyingGuide content={REMAINING_SEO_GUIDES[config.handle]} /> :
        FLOWER_HANDLES.has(config.handle) ? <FlowerDeliveryBanner handle={config.handle} /> :
        OKAZJA_BANNER_HANDLES.has(config.handle) ? <OkazjaBanner handle={config.handle} /> :
        SPECJALNY_BANNER_HANDLES.has(config.handle) ? <SpecjalnyBanner handle={config.handle} /> :
        <CollectionSeoSection handle={config.handle} />
      )}
      <Footer />
    </>
  );
}
